/**
 * Manual well seeding (Seed dialog → Manual tab): single wells or a multi-spot
 * pattern placed by click, carved against the contour and existing wells.
 */
import type { MbPoint } from '@/types/materialBalance';
import { pointInPoly } from '@/utils/materialBalanceAutoPlace';

export type SeedWellKind = 'prod' | 'inj';
export type SeedTool = 'pattern' | 'prod' | 'inj';

/** prod : inj — one side is always a single well */
export type SeedRatio = { prod: number; inj: number };

export const SEED_RATIOS: SeedRatio[] = [
  { prod: 1, inj: 1 },
  { prod: 1, inj: 2 },
  { prod: 1, inj: 3 },
  { prod: 1, inj: 4 },
  { prod: 1, inj: 5 },
  { prod: 2, inj: 1 },
  { prod: 3, inj: 1 },
  { prod: 4, inj: 1 },
  { prod: 5, inj: 1 },
];

export const seedRatioKey = (r: SeedRatio) => `${r.prod}:${r.inj}`;

export type SeedWellRow = {
  id: string;
  well_name: string;
  kind: SeedWellKind;
  t0: number | null;
  t1: number | null;
  x: number | null;
  y: number | null;
  q_start_td: number | null;
};

export type SeedCandidate = {
  kind: SeedWellKind;
  x: number;
  y: number;
  /** The single well of the ratio, in the middle of the pattern */
  center: boolean;
};

/** Pattern around `at`: the single-side well in the center, the others on a ring of radius `distanceKm`. */
export function buildSeedPattern(
  at: MbPoint,
  ratio: SeedRatio,
  distanceKm: number
): SeedCandidate[] {
  const centerKind: SeedWellKind = ratio.prod === 1 ? 'prod' : 'inj';
  const ringKind: SeedWellKind = centerKind === 'prod' ? 'inj' : 'prod';
  const n = centerKind === 'prod' ? ratio.inj : ratio.prod;
  // Square 5-spot: ring wells on the diagonals
  const angle0 = n === 4 ? Math.PI / 4 : Math.PI / 2;
  const out: SeedCandidate[] = [{ kind: centerKind, x: at.x, y: at.y, center: true }];
  for (let k = 0; k < n; k += 1) {
    const a = angle0 + (2 * Math.PI * k) / n;
    out.push({
      kind: ringKind,
      x: at.x + distanceKm * Math.cos(a),
      y: at.y + distanceKm * Math.sin(a),
      center: false,
    });
  }
  return out;
}

/** Drop candidates outside the contour or within `minDistKm` of an existing well. */
export function carveSeedCandidates(
  candidates: SeedCandidate[],
  contour: MbPoint[],
  existing: MbPoint[],
  minDistKm: number
): SeedCandidate[] {
  const min2 = minDistKm * minDistKm;
  return candidates.filter(
    (c) =>
      pointInPoly(c.x, c.y, contour) &&
      !existing.some((p) => (p.x - c.x) ** 2 + (p.y - c.y) ** 2 < min2)
  );
}

/** Next free names `P-<n>` / `I-<n>` that do not clash with `taken` (case-insensitive). */
export function nextSeedWellNames(taken: string[], kind: SeedWellKind, count: number): string[] {
  const prefix = kind === 'inj' ? 'I-' : 'P-';
  const used = new Set(taken.map((n) => n.trim().toLowerCase()));
  const out: string[] = [];
  let i = 1;
  while (out.length < count) {
    const name = `${prefix}${i}`;
    if (!used.has(name.toLowerCase())) {
      out.push(name);
      used.add(name.toLowerCase());
    }
    i += 1;
  }
  return out;
}
